// LegalPage — the shared shell for Användarvillkor and Integritetspolicy (#324).
//
// Public and unauthenticated on purpose: these are linked from the signup form,
// where by definition nobody is signed in yet, and a consent link that demands a
// login is not consent.
//
// The two pages are structurally identical and differ only in their text, so the
// chrome lives here once. Both open in a new tab from the signup checkbox — a
// person mid-registration should be able to read the terms without losing the
// form they have half-filled.
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Footer } from '@/components/landing/Footer';

interface LegalPageProps {
    title: string;
    /** Shown under the title — what this document is, in one line. */
    intro: string;
    /** Rendered inside the prose container. */
    children: React.ReactNode;
}

export const LegalPage: React.FC<LegalPageProps> = ({ title, intro, children }) => {
    useEffect(() => {
        document.title = `${title} — Nexilink`;
    }, [title]);

    return (
        <div className="min-h-screen flex flex-col bg-[#FAFAF8]">
            <header className="border-b border-gray-200 bg-white">
                <div className="container mx-auto px-6 sm:px-12 py-4">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1A4D4F] hover:underline"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Till startsidan
                    </Link>
                </div>
            </header>

            <main className="flex-1 container mx-auto px-6 sm:px-12 py-12 max-w-3xl">
                <div className="flex items-center gap-3 mb-2">
                    <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#A4C3B2]/30 flex-shrink-0">
                        <FileText className="w-5 h-5 text-[#1A4D4F]" />
                    </span>
                    <h1 className="text-3xl font-bold text-[#212121]">{title}</h1>
                </div>
                <p className="text-gray-600 mb-10">{intro}</p>

                <div className="space-y-6 text-[15px] leading-relaxed text-gray-800">{children}</div>
            </main>

            <Footer />
        </div>
    );
};

/** A titled section, so both documents share one heading rhythm. */
export const LegalSection: React.FC<{ heading: string; children: React.ReactNode }> = ({
    heading,
    children,
}) => (
    <section>
        <h2 className="text-lg font-semibold text-[#212121] mb-2">{heading}</h2>
        <div className="space-y-3">{children}</div>
    </section>
);

/**
 * PLACEHOLDER BANNER — remove together with the placeholder text.
 *
 * Deliberately loud and deliberately NOT removable by a config flag. The whole
 * risk with shipping a legal page early is that a draft quietly starts looking
 * authoritative; a page that says "this is not the final text" cannot be mistaken
 * for one that has been reviewed. See #324: the routes, the consent gate and the
 * layout are ours, the words are the client's.
 */
export const LegalDraftNotice: React.FC = () => (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 mb-8">
        <p className="text-sm font-semibold text-amber-900">Utkast — inte den slutgiltiga texten</p>
        <p className="text-sm text-amber-900/90 mt-1">
            Innehållet nedan är en platshållare och är inte juridiskt granskat. Den färdiga texten
            tas fram av Nexilink innan tjänsten öppnas för allmänheten.
        </p>
    </div>
);

export default LegalPage;
